import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { Table, Button, Container, Row, Col, Card, Badge, Modal, Form, InputGroup, Image, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaPhoneAlt, FaEye, FaRegHeart, FaShareAlt, FaRegPaperPlane, FaCheckCircle, FaWhatsapp, FaEnvelope, FaPhone, FaTelegram, FaComment, FaVideo } from 'react-icons/fa';
import { AdminCandidateCard } from '../Candidates/Candidates_admin';
import axios from 'axios';
import Swal from 'sweetalert2';
import './ManageEmployee.css';
import '../Candidates/Candidates_admin.css';
import BaseUrl from '../../ApiServices/BaseUrl';

function ManageEmployee() {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeCounts, setEmployeeCounts] = useState({
    total: 0,
    verified: 0,
    pending: 0
  });
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 10;

  // Settings Modal states
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isPublishFeeEnabled, setIsPublishFeeEnabled] = useState(false);
  const [publishFeeAmount, setPublishFeeAmount] = useState('0');

  const navigate = useNavigate();

  const tableRef = useRef(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  const onMouseDown = (e) => {
    isDragging.current = true;
    startX.current = e.pageX - tableRef.current.offsetLeft;
    scrollLeft.current = tableRef.current.scrollLeft;
    tableRef.current.style.cursor = 'grabbing';
  };
  const onMouseLeave = () => { isDragging.current = false; tableRef.current.style.cursor = 'grab'; };
  const onMouseUp = () => { isDragging.current = false; tableRef.current.style.cursor = 'grab'; };
  const onMouseMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const x = e.pageX - tableRef.current.offsetLeft;
    tableRef.current.scrollLeft = scrollLeft.current - (x - startX.current);
  };

  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [badgeText, setBadgeText] = useState('');
  const [badgeCandidateId, setBadgeCandidateId] = useState(null);

  const [badgePrice, setBadgePrice] = useState('10');
  const [tagPrice, setTagPrice] = useState('10');
  const [bothPrice, setBothPrice] = useState('18');

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    filterEmployees();
    setCurrentPage(1);
  }, [searchTerm, employees]);

  const fetchEmployees = async () => {
    try {
      const jobseekersResponse = await axios.get(`${BaseUrl}/api/job-seeker`);
      const jobseekersData = Array.isArray(jobseekersResponse.data)
        ? jobseekersResponse.data
        : jobseekersResponse.data.data || [];

      if (!Array.isArray(jobseekersData)) {
        console.warn("No employee records found");
        setEmployees([]);
        setFilteredEmployees([]);
        setLoading(false);
        return;
      }

      const total = jobseekersData.length;
      const verified = jobseekersData.filter(emp =>
        emp.verification_status?.toLowerCase() === 'verified'
      ).length;
      const unpublished = jobseekersData.filter(emp =>
        emp.verification_status?.toLowerCase() === 'unpublished'
      ).length;
      const pending = jobseekersData.filter(emp =>
        (emp.verification_status?.toLowerCase() === 'pending' || !emp.verification_status) &&
        emp.verification_status?.toLowerCase() !== 'unpublished'
      ).length;

      setEmployeeCounts({ total, verified, pending, unpublished });

      console.log("✅ Final Employees Data:", jobseekersData);
      // We no longer loop through every jobseeker to fetch individual /api/users/:id
      // to avoid causing heavy N+1 query loads and numerous 404 errors in the console.
      
      // Sort strictly by latest registration date first
      const sortedData = [...jobseekersData].sort((a, b) => {
        const aDate = new Date(a.created_at || 0).getTime();
        const bDate = new Date(b.created_at || 0).getTime();

        if (bDate !== aDate) {
          return bDate - aDate;
        }

        return Number(b.user_id || 0) - Number(a.user_id || 0);
      });
      
      setEmployees(sortedData);
      setFilteredEmployees(sortedData);
      setLoading(false);
    } catch (err) {
      console.error("❌ Error fetching employees:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  const filterEmployees = () => {
    if (!searchTerm.trim()) {
      setFilteredEmployees(employees);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = employees.filter(employee => {
      // Search in all relevant fields
      return (
        // Name fields
        (employee.first_name && employee.first_name.toLowerCase().includes(searchLower)) ||
        (employee.last_name && employee.last_name.toLowerCase().includes(searchLower)) ||
        // Email
        (employee.email_id && employee.email_id.toLowerCase().includes(searchLower)) ||
        // Phone numbers
        (employee.whatsapp_number && employee.whatsapp_number.includes(searchTerm)) ||
        (employee.telegram_number && employee.telegram_number.includes(searchTerm)) ||
        (employee.botim_number && employee.botim_number.includes(searchTerm)) ||
        // Category and Position
        (employee.category && employee.category.toLowerCase().includes(searchLower)) ||
        (employee.job_position &&
          (Array.isArray(employee.job_position)
            ? employee.job_position.some(pos => pos.toLowerCase().includes(searchLower))
            : employee.job_position.toLowerCase().includes(searchLower)
          )
        ) ||
        // Age and Gender
        (employee.age && employee.age.toString().includes(searchTerm)) ||
        (employee.gender && employee.gender.toLowerCase().includes(searchLower)) ||
        // Location fields
        (employee.nationality && employee.nationality.toLowerCase().includes(searchLower)) ||
        (employee.current_country && employee.current_country.toLowerCase().includes(searchLower)) ||
        // Verification status
        (employee.verification_status && employee.verification_status.toLowerCase().includes(searchLower)) ||
        // Skills
        (employee.main_skills &&
          (Array.isArray(employee.main_skills)
            ? employee.main_skills.some(skill => skill.toLowerCase().includes(searchLower))
            : employee.main_skills.toLowerCase().includes(searchLower)
          )
        ) ||
        (employee.cooking_skills &&
          (Array.isArray(employee.cooking_skills)
            ? employee.cooking_skills.some(skill => skill.toLowerCase().includes(searchLower))
            : employee.cooking_skills.toLowerCase().includes(searchLower)
          )
        ) ||
        // Language skills
        (employee.language &&
          (typeof employee.language === 'string'
            ? employee.language.toLowerCase().includes(searchLower)
            : JSON.stringify(employee.language).toLowerCase().includes(searchLower)
          )
        ) ||
        // Other fields
        (employee.marital_status && employee.marital_status.toLowerCase().includes(searchLower)) ||
        (employee.religion && employee.religion.toLowerCase().includes(searchLower)) ||
        (employee.job_type && employee.job_type.toLowerCase().includes(searchLower)) ||
        (employee.visa_type && employee.visa_type.toLowerCase().includes(searchLower)) ||
        (employee.current_work_status && employee.current_work_status.toLowerCase().includes(searchLower))
      );
    });

    setFilteredEmployees(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleEditEmployee = (employee) => {
    console.log('Edit button clicked for employee:', employee);
    console.log('User ID to navigate with:', employee.user_id);

    if (employee.user_id) {
      navigate('/adminpersonalinfo', {
        state: {
          userId: employee.user_id,
          candidateData: employee // Pass the entire employee data if needed
        }
      });
    } else {
      console.error('No user_id found for employee:', employee);
      Swal.fire({
        title: 'Error!',
        text: 'Cannot edit employee: Missing user ID',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  const handleViewDetails = (employee) => {
    setSelectedEmployee(employee);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedEmployee(null);
  };

  const [publishCurrency, setPublishCurrency] = useState('AED');

  const fetchPublishSettings = async () => {
    try {
      const response = await axios.get(`${BaseUrl}/api/admin/settings/candidate_publish_fee`);
      if (response.data) {
        setIsPublishFeeEnabled(response.data.is_enabled === 1 || response.data.is_enabled === true);
        setPublishFeeAmount(response.data.setting_value || '0');
      }

      try {
        const currencyResponse = await axios.get(`${BaseUrl}/api/admin/settings/candidate_publish_currency`);
        if (currencyResponse.data) {
          setPublishCurrency(currencyResponse.data.setting_value || 'AED');
        }
      } catch (err) {
        // If not found (404), default to AED
        setPublishCurrency('AED');
      }

      // Fetch Badge/Tag Prices
      try {
        const badgeResp = await axios.get(`${BaseUrl}/api/admin/settings/badge_price`);
        if (badgeResp.data) setBadgePrice(badgeResp.data.setting_value || '10');
        
        const tagResp = await axios.get(`${BaseUrl}/api/admin/settings/tag_price`);
        if (tagResp.data) setTagPrice(tagResp.data.setting_value || '10');

        const bothResp = await axios.get(`${BaseUrl}/api/admin/settings/both_price`);
        if (bothResp.data) setBothPrice(bothResp.data.setting_value || '18');
      } catch (err) {
        console.error('Error fetching badge/tag prices:', err);
      }
    } catch (err) {
      console.error('Error fetching publish settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${BaseUrl}/api/admin/settings/candidate_publish_fee`, {
        setting_value: publishFeeAmount,
        is_enabled: isPublishFeeEnabled,
      });

      await axios.put(`${BaseUrl}/api/admin/settings/candidate_publish_currency`, {
        setting_value: publishCurrency,
        is_enabled: true,
      });

      // Save Badge/Tag Prices
      await axios.put(`${BaseUrl}/api/admin/settings/badge_price`, {
        setting_value: badgePrice,
        is_enabled: true,
      });
      await axios.put(`${BaseUrl}/api/admin/settings/tag_price`, {
        setting_value: tagPrice,
        is_enabled: true,
      });
      await axios.put(`${BaseUrl}/api/admin/settings/both_price`, {
        setting_value: bothPrice,
        is_enabled: true,
      });

      Swal.fire('Success', 'Settings updated successfully', 'success');
      setShowSettingsModal(false);
    } catch (err) {
      console.error('Error saving settings:', err);
      Swal.fire('Error', 'Failed to save settings', 'error');
    }
  };

  const handleSaveBadge = async () => {
    try {
      if (!badgeCandidateId) return;

      const response = await axios.put(`${BaseUrl}/api/admin/job-seeker/assign-badge`, {
        candidate_id: badgeCandidateId,
        badge_text: badgeText
      });

      if (response.data.success) {
        Swal.fire('Success', 'Offer Badge assigned successfully', 'success');
        setShowBadgeModal(false);
        fetchEmployees(); // Refresh table
      }
    } catch (err) {
      console.error('Error assigning badge:', err);
      Swal.fire('Error', err.response?.data?.error || 'Failed to assign badge', 'error');
    }
  };

  const openSettingsModal = () => {
    fetchPublishSettings();
    setShowSettingsModal(true);
  };

  const handleViewCandidate = (employee) => {
    if (employee.user_id) {
      navigate(`/candidatesviewadmin/${employee.user_id}`);
    }
  };

  const handleVerify = async (employee) => {
    const isVerified = employee.verification_status?.toLowerCase() === 'verified';
    const newStatus = isVerified ? 'Pending' : 'Verified';
    const actionText = isVerified ? 'Unverify' : 'Verify';

    try {
      const result = await Swal.fire({
        title: `${actionText} Candidate?`,
        text: `Are you sure you want to ${actionText.toLowerCase()} ${employee.first_name} ${employee.last_name}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: isVerified ? '#ffc107' : '#28a745',
        cancelButtonColor: '#d33',
        confirmButtonText: `Yes, ${actionText}!`
      });

      if (result.isConfirmed) {
        setLoading(true);
        const response = await axios.put(`${BaseUrl}/api/job-seeker/status/${employee.user_id}`, {
          verification_status: newStatus
        });

        if (response.data) {
          Swal.fire(`${actionText}ed!`, `The candidate has been ${actionText.toLowerCase()}ed.`, 'success');
          // Update local state
          const updatedEmployees = employees.map(emp => {
            if (emp.user_id === employee.user_id) {
              return { ...emp, verification_status: newStatus };
            }
            return emp;
          });
          setEmployees(updatedEmployees);
          setFilteredEmployees(prev => prev.map(emp => emp.user_id === employee.user_id ? { ...emp, verification_status: newStatus } : emp));

          // Re-evaluate filtered employees and counts
          setEmployeeCounts(prev => ({
            ...prev,
            verified: isVerified ? prev.verified - 1 : prev.verified + 1,
            pending: isVerified ? prev.pending + 1 : prev.pending - 1
          }));

          if (selectedEmployee && selectedEmployee.user_id === employee.user_id) {
            setSelectedEmployee({ ...selectedEmployee, verification_status: newStatus });
          }
        }
      }
    } catch (error) {
      console.error(`Error ${actionText.toLowerCase()}ing employee:`, error);
      Swal.fire('Error', `Failed to ${actionText.toLowerCase()} candidate.`, 'error');
    } finally {
      // Refresh the page
      fetchEmployees();
    }
  };

  const confirmDelete = (employee) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${employee.first_name} ${employee.last_name}". This will remove the user from both users table and job seekers table. This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete permanently!',
      cancelButtonText: 'Cancel',
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          // First try to delete from users table (which should cascade or we handle both)
          const response = await axios.delete(`${BaseUrl}/api/user/${employee.user_id}`);
          return response.data;
        } catch (error) {
          Swal.showValidationMessage(`Request failed: ${error.response?.data?.message || error.message}`);
          return null;
        }
      },
      allowOutsideClick: () => !Swal.isLoading()
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        const data = result.value;

        if (data.success) {
          // Update local state by removing the deleted employee
          setEmployees(prev => prev.filter(item => item.user_id !== employee.user_id));
          setFilteredEmployees(prev => prev.filter(item => item.user_id !== employee.user_id));

          // Update counts
          const updatedCounts = {
            total: employeeCounts.total - 1,
            verified: employee.verification_status?.toLowerCase() === 'verified'
              ? employeeCounts.verified - 1
              : employeeCounts.verified,
            pending: employee.verification_status?.toLowerCase() !== 'verified'
              ? employeeCounts.pending - 1
              : employeeCounts.pending
          };
          setEmployeeCounts(updatedCounts);

          Swal.fire({
            title: 'Deleted!',
            text: 'User has been permanently deleted from both tables.',
            icon: 'success',
            confirmButtonColor: '#3085d6'
          });
        } else {
          Swal.fire(
            'Error!',
            data.message || 'Failed to delete user',
            'error'
          );
        }
      }
    });
  };

  const getPaymentBadge = (status, feePaid) => {
    if (feePaid > 0) {
      return <Badge bg="success">Paid ({publishCurrency} {feePaid})</Badge>;
    }
    if (status?.toLowerCase() === 'unpublished') {
      return <Badge bg="danger">Unpaid</Badge>;
    }
    return <Badge bg="light" text="dark">N/A</Badge>;
  };

  const getStatusBadge = (status) => {
    let badge;
    const normalizedStatus = status?.toLowerCase();

    switch (normalizedStatus) {
      case 'verified':
      case 'active':
        badge = <Badge bg="success">Verified</Badge>;
        break;
      case 'pending':
      case 'unpublished': // Show as Pending in this column
        badge = <Badge bg="warning">Pending</Badge>;
        break;
      case 'suspended':
      case 'banned':
        badge = <Badge bg="danger">Suspended</Badge>;
        break;
      case 'inactive':
        badge = <Badge bg="secondary">Inactive</Badge>;
        break;
      default:
        badge = <Badge bg="info">{status || 'Pending'}</Badge>;
    }
    return badge;
  };

  const getBanStatus = (isBan) => {
    return isBan ? <Badge bg="danger">Banned</Badge> : <Badge bg="success">Active</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return '-';
    }
  };

  const getFirstPosition = (positions) => {
    if (!positions) return '-';
    if (Array.isArray(positions) && positions.length > 0) {
      return positions[0];
    }
    return positions || '-';
  };

  const renderArrayData = (data) => {
    if (!data) return '-';
    if (Array.isArray(data)) {
      // Remove square brackets and quotes from array display
      return data.map(item => {
        if (typeof item === 'string') {
          // Remove quotes and brackets from string items
          return item.replace(/[\[\]"]/g, '');
        }
        return item;
      }).join(', ');
    }
    // If it's a string that looks like an array, clean it up
    if (typeof data === 'string' && data.startsWith('[') && data.endsWith(']')) {
      return data.replace(/[\[\]"]/g, '');
    }
    return data;
  };

  const parseListValue = (value) => {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];

      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => String(item || '').trim())
            .filter(Boolean);
        }
      } catch (error) {
        // Fall back to plain string handling below.
      }

      return trimmed
        .split(',')
        .map((item) => item.replace(/[\[\]"]/g, '').trim())
        .filter(Boolean);
    }

    return [String(value).trim()].filter(Boolean);
  };

  const getLicenseDisplay = (employee) => {
    const positions = parseListValue(employee.job_position).map((position) =>
      position.toLowerCase()
    );

    const isNurseRole = positions.some(
      (position) =>
        position.includes('nurse') ||
        position.includes('midwife') ||
        position.includes('midwifery')
    );

    const isDriverRole = positions.some((position) => position.includes('driver'));

    if (isNurseRole) {
      const nursingLicenses = parseListValue(
        employee.nursing_license_countries || employee.nursing_license_country
      );

      if (nursingLicenses.length > 0) {
        return nursingLicenses.join(', ');
      }

      return employee.nurse_license_status || '-';
    }

    if (isDriverRole) {
      const drivingLicenses = parseListValue(employee.driving_license_country);
      return drivingLicenses.length > 0 ? drivingLicenses.join(', ') : '-';
    }

    return '-';
  };

  const renderLanguageSkills = (languageSkills) => {
    if (!languageSkills) return 'N/A';
    try {
      if (typeof languageSkills === 'string') {
        languageSkills = JSON.parse(languageSkills);
      }
      if (Array.isArray(languageSkills)) {
        return languageSkills.map(lang =>
          `${lang.language} (${lang.speaking_level})`
        ).join(', ');
      }
      return 'N/A';
    } catch (e) {
      return languageSkills;
    }
  };

  // Function to get all contact numbers with country codes
  const getContactNumbers = (employee) => {
    const contacts = [];

    // WhatsApp
    if (employee.whatsapp_number) {
      contacts.push({
        type: 'WhatsApp',
        number: `${employee.whatsapp_country_code || ''} ${employee.whatsapp_number}`,
        icon: <FaWhatsapp className="text-success" />,
        rawNumber: employee.whatsapp_number,
        countryCode: employee.whatsapp_country_code || ''
      });
    }

    // Telegram
    if (employee.telegram_number) {
      contacts.push({
        type: 'Telegram',
        number: `${employee.telegram_country_code || ''} ${employee.telegram_number}`,
        icon: <FaTelegram className="text-info" />,
        rawNumber: employee.telegram_number,
        countryCode: employee.telegram_country_code || ''
      });
    }

    // Botim
    if (employee.botim_number) {
      contacts.push({
        type: 'Botim',
        number: `${employee.botim_country_code || ''} ${employee.botim_number}`,
        icon: <FaComment className="text-primary" />,
        rawNumber: employee.botim_number,
        countryCode: employee.botim_country_code || ''
      });
    }

    // Mobile Number (if exists and not duplicate)
    if (employee.mobile_number && !contacts.some(c => c.rawNumber === employee.mobile_number)) {
      contacts.push({
        type: 'Mobile',
        number: `${employee.mobile_country_code || ''} ${employee.mobile_number}`,
        icon: <FaPhone className="text-secondary" />,
        rawNumber: employee.mobile_number,
        countryCode: employee.mobile_country_code || ''
      });
    }

    return contacts;
  };

  // Email Popover Component
  const EmailPopover = (email) => (
    <Popover id="email-popover">
      <Popover.Header as="h3">Email Address</Popover.Header>
      <Popover.Body>
        <div className="d-flex align-items-center">
          <FaEnvelope className="me-2 text-primary" />
          <a href={`mailto:${email}`} className="text-decoration-none">{email}</a>
        </div>
      </Popover.Body>
    </Popover>
  );

  // Phone Popover Component
  const PhonePopover = (contacts) => (
    <Popover id="phone-popover" style={{ maxWidth: '300px' }}>
      <Popover.Header as="h3">Contact Numbers</Popover.Header>
      <Popover.Body>
        {contacts.map((contact, idx) => {
          const isWhatsApp = contact.type === 'WhatsApp';
          const cleanNumber = contact.rawNumber.replace(/\D/g, '');
          const cleanCode = contact.countryCode.replace(/\D/g, '');

          let link = '';
          if (isWhatsApp) {
            const finalWhatsApp = cleanCode ? `${cleanCode}${cleanNumber}` : `91${cleanNumber}`;
            link = `https://wa.me/${finalWhatsApp}`;
          } else {
            const finalPhone = cleanCode ? `+${cleanCode}${cleanNumber}` : `+${cleanNumber}`;
            link = `tel:${finalPhone}`;
          }

          return (
            <div key={idx} className="d-flex align-items-center mb-2 pb-2 border-bottom">
              <span className="me-2">{contact.icon}</span>
              <div>
                <small className="text-muted d-block">{contact.type}</small>
                <a
                  href={link}
                  className="text-decoration-none"
                  style={{ fontSize: '0.9rem' }}
                  target={isWhatsApp ? "_blank" : "_self"}
                  rel={isWhatsApp ? "noopener noreferrer" : ""}
                >
                  {contact.number}
                </a>
              </div>
            </div>
          );
        })}
        {contacts.length === 0 && (
          <div className="text-muted">No contact numbers available</div>
        )}
      </Popover.Body>
    </Popover>
  );

  if (loading) return (
    <>
      <AdminNavbar />
      <div className="text-center mt-5" style={{ marginTop: '100px' }}>Loading employees...</div>
    </>
  );

  if (error) return (
    <>
      <AdminNavbar />
      <div className="text-center mt-5 text-danger" style={{ marginTop: '100px' }}>Error: {error}</div>
    </>
  );

  return (
    <>
      <AdminNavbar />
      <Container className="" style={{ marginTop: '80px' }}>
        <Row className="mb-4 manageemployee-text">
          <Col style={{ marginTop: '20px' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2>Manage Employees</h2>
                <p className="text-muted">View and manage all employees (Job Seekers)</p>
              </div>
              <div className="d-flex align-items-center gap-2 search-bar-container" style={{ minWidth: '400px' }}>
                <Button
                  variant="primary"
                  onClick={openSettingsModal}
                  className="d-flex align-items-center gap-2 px-4 py-2 shadow-sm"
                  style={{ fontWeight: '600', transition: 'all 0.3s', whiteSpace: 'nowrap', borderRadius: '8px' }}
                >
                  <i className="bi bi-gear-fill"></i> Publishing Fee
                </Button>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="search-input"
                  />
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                </InputGroup>
              </div>
            </div>
            {searchTerm && (
              <div className="text-muted small mt-1 text-end mb-3">
                Showing {filteredEmployees.length} of {employees.length} employees
              </div>
            )}

            {/* Employee Counts */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="text-center bg-primary text-white">
                  <Card.Body>
                    <h3>{employeeCounts.total}</h3>
                    <p className="mb-0">Total Candidates</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center bg-success text-white">
                  <Card.Body>
                    <h3>{employeeCounts.verified}</h3>
                    <p className="mb-0">Verified Candidates</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center bg-warning text-dark">
                  <Card.Body>
                    <h3>{employeeCounts.pending}</h3>
                    <p className="mb-0">Pending Candidates</p>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center bg-secondary text-white">
                  <Card.Body>
                    <h3>{employeeCounts.unpublished}</h3>
                    <p className="mb-0">Unpublished</p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <div className='manageemployeehead'>
          <Card>
            <Card.Body>
              <div className="manageemployee">
                <div
                  ref={tableRef}
                  className="table-responsive"
                  onMouseDown={onMouseDown}
                  onMouseLeave={onMouseLeave}
                  onMouseUp={onMouseUp}
                  onMouseMove={onMouseMove}
                  style={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem',
                    cursor: 'grab',
                    userSelect: 'none'
                  }}>
                  <Table striped bordered hover style={{
                    minWidth: '1500px',
                    width: '100%',
                    marginBottom: 0
                  }}>
                    <thead className="table-dark" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr>
                        <th style={{ minWidth: '80px' }}>S.No</th>
                        <th style={{ minWidth: '180px' }}>Name</th>
                        <th style={{ minWidth: '100px' }}>Source</th>
                        <th style={{ minWidth: '60px', textAlign: 'center' }}>Email</th>
                        <th style={{ minWidth: '60px', textAlign: 'center' }}>Phone</th>
                        <th style={{ minWidth: '120px' }}>Category</th>
                        <th style={{ minWidth: '150px' }}>Position</th>
                        <th style={{ minWidth: '150px' }}>License</th>
                        <th style={{ minWidth: '120px' }}>Age/Gender</th>
                        <th style={{ minWidth: '120px' }}>Nationality</th>
                        <th style={{ minWidth: '120px' }}>Current Country</th>
                        <th style={{ minWidth: '130px' }}>Registered Date</th>
                        <th style={{ minWidth: '130px' }}>Publishing</th>
                        <th style={{ minWidth: '150px', textAlign: 'center' }}>Add-ons</th>
                        <th style={{ minWidth: '140px' }}>Verification Status</th>
                        <th style={{ minWidth: '400px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const startIdx = (currentPage - 1) * ROWS_PER_PAGE;
                        const paginatedEmployees = filteredEmployees.slice(startIdx, startIdx + ROWS_PER_PAGE);
                        return paginatedEmployees.map((employee, index) => {
                        const email = employee.email_id || '-';
                        const contactNumbers = getContactNumbers(employee);

                        return (
                          <tr key={employee.id}>
                            <td>{startIdx + index + 1}</td>
                            <td className="fw-semibold">
                              {`${employee.first_name || ''} ${employee.last_name || ''}`.trim() || '-'}
                            </td>
                            <td>
                              {employee.source === 'agency' || employee.agency_uid ? (
                                <Badge bg="info">Agency</Badge>
                              ) : (
                                <Badge bg="primary">Direct</Badge>
                              )}
                            </td>
                            <td className="text-center">
                              {email !== '-' ? (
                                <OverlayTrigger
                                  trigger={['click', 'focus']}
                                  placement="left"
                                  overlay={EmailPopover(email)}
                                  rootClose
                                >
                                  <button
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: '6px',
                                      cursor: 'pointer',
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s',
                                      color: '#0d6efd'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    title="Click to view email"
                                  >
                                    <FaEnvelope size={18} />
                                  </button>
                                </OverlayTrigger>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td className="text-center">
                              {contactNumbers.length > 0 ? (
                                <OverlayTrigger
                                  trigger={['click', 'focus']}
                                  placement="left"
                                  overlay={PhonePopover(contactNumbers)}
                                  rootClose
                                >
                                  <button
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      padding: '6px',
                                      cursor: 'pointer',
                                      borderRadius: '50%',
                                      width: '32px',
                                      height: '32px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      transition: 'all 0.2s',
                                      color: '#28a745'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    title="Click to view contact numbers"
                                  >
                                    <FaPhone size={16} />
                                  </button>
                                </OverlayTrigger>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>{employee.category || '-'}</td>
                            <td>{renderArrayData(employee.job_position)}</td>
                            <td>{getLicenseDisplay(employee)}</td>
                            <td>
                              {employee.age ? `${employee.age} / ${employee.gender || '-'}` : employee.gender || '-'}
                            </td>
                            <td>{employee.nationality || '-'}</td>
                            <td>{employee.current_country || '-'}</td>
                            <td>{formatDate(employee.created_at)}</td>
                            <td>{getPaymentBadge(employee.verification_status, employee.publishing_fee_paid)}</td>
                            <td className="text-center">
                              {employee.purchased_badge === 1 && (
                                <Badge bg="success" className="d-block mb-1" style={{ fontSize: '0.75rem' }}>
                                  Verified Tag
                                </Badge>
                              )}
                              {employee.purchased_tag === 1 && (
                                <Badge bg="primary" className="d-block" style={{ fontSize: '0.75rem' }}>
                                  Offer Badge: {employee.badge_status}
                                </Badge>
                              )}
                              {employee.purchased_badge !== 1 && employee.purchased_tag !== 1 && (
                                <span className="text-muted small">-</span>
                              )}
                            </td>
                            <td>{getStatusBadge(employee.verification_status)}</td>
                            <td>
                              <div className="d-flex gap-1 align-items-center">
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleEditEmployee(employee)}
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  <i className="bi bi-pencil me-1"></i> Edit
                                </Button>
                                {employee.purchased_tag === 1 && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => {
                                      setBadgeCandidateId(employee.user_id);
                                      setBadgeText(employee.badge_text || '');
                                      setShowBadgeModal(true);
                                    }}
                                    style={{ whiteSpace: 'nowrap' }}
                                  >
                                    <i className="bi bi-patch-check me-1"></i> {employee.badge_status === 'assigned' ? 'Edit Badge' : 'Assign Badge'}
                                  </Button>
                                )}
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  onClick={() => handleViewDetails(employee)}
                                  style={{ whiteSpace: 'nowrap', color: '#031316' }}
                                >
                                  <i className="bi bi-eye me-1"></i> View Details
                                </Button>
                                {
                                  employee.verification_status?.toLowerCase() !== 'verified' ? (
                                    <Button
                                      variant="outline-success"
                                      size="sm"
                                      onClick={() => handleVerify(employee)}
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      <i className="bi bi-check-circle me-1"></i> Verify
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="outline-warning"
                                      size="sm"
                                      onClick={() => handleVerify(employee)}
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      <i className="bi bi-x-circle me-1"></i> Unverify
                                    </Button>
                                  )
                                }
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => confirmDelete(employee)}
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  <i className="bi bi-trash me-1"></i> Delete
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                      })()}
                    </tbody>
                  </Table>

                  {filteredEmployees.length === 0 && (
                    <div className="text-center py-4" style={{ minWidth: '1500px' }}>
                      {searchTerm ? (
                        <p className="text-muted">No employees found matching "<strong>{searchTerm}</strong>"</p>
                      ) : (
                        <p className="text-muted">No employees found.</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Pagination Controls */}
                {filteredEmployees.length > ROWS_PER_PAGE && (
                  <div className="me-pagination-wrapper">
                    <button
                      className="me-pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      &lsaquo;
                    </button>
                    <span className="me-pagination-info">
                      <span className="me-pagination-current">{currentPage}</span>
                      {' / '}
                      {Math.ceil(filteredEmployees.length / ROWS_PER_PAGE)}
                    </span>
                    <button
                      className="me-pagination-btn"
                      disabled={currentPage >= Math.ceil(filteredEmployees.length / ROWS_PER_PAGE)}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      &rsaquo;
                    </button>
                  </div>
                )}
              </div>
            </Card.Body >
          </Card >
        </div >
      </Container >

      {/* Employee Details Modal */}
      < Modal show={showDetailsModal} onHide={handleCloseDetails} size="md" centered >
        <Modal.Header closeButton style={{ borderBottom: 'none' }}>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#fff', padding: '15px' }}>
          {selectedEmployee && (
            <AdminCandidateCard
              candidate={selectedEmployee}
              onViewClick={() => handleViewCandidate(selectedEmployee)}
              onMessageClick={() => { }}
              onReportClick={() => { }}
              onShareClick={() => { }}
              handleVerify={handleVerify}
            />
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none' }}>
          <Button variant="secondary" onClick={handleCloseDetails}>
            Close
          </Button>
        </Modal.Footer>
      </Modal >

      {/* Publishing Settings Modal */}
      < Modal
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)
        }
        centered
        className="premium-modal"
        backdrop="static"
      >
        <Modal.Header closeButton style={{ border: 'none', padding: '25px 30px 10px' }}>
          <Modal.Title className="fw-bold" style={{ color: '#004a8c', fontSize: '1.5rem' }}>
            Publishing Configuration
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px', backgroundColor: '#fdfdfd' }}>
          <div className="text-center mb-4">
            {/* <div className="mx-auto bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center mb-3" style={{ width: '60px', height: '60px' }}>
              <i className="bi bi-cash-coin fs-2"></i>
            </div> */}
            <p className="text-muted">Manage the one-time publishing fee for candidate profiles.</p>
          </div>

          <Form>
            <div className="settings-card p-4 rounded shadow-sm border mb-3 bg-white">
              <Form.Group className="mb-4 d-flex align-items-center justify-content-between">
                <div>
                  <Form.Label className="fw-bold mb-0">Publishing Fee System</Form.Label>
                  <div className="text-muted small">Toggle the mandatory publishing fee</div>
                </div>
                <Form.Check
                  type="switch"
                  id="publish-fee-switch"
                  checked={isPublishFeeEnabled}
                  onChange={(e) => setIsPublishFeeEnabled(e.target.checked)}
                  style={{ transform: 'scale(1.3)' }}
                />
              </Form.Group>

              <div
                style={{
                  maxHeight: isPublishFeeEnabled ? '200px' : '0',
                  overflow: 'hidden',
                  transition: 'all 0.4s ease-out',
                  opacity: isPublishFeeEnabled ? 1 : 0
                }}
              >
                <Form.Group>
                  <Form.Label className="fw-bold">Fee Amount</Form.Label>
                  <InputGroup size="lg">
                    <Form.Select 
                      className="bg-light border-end" 
                      style={{ maxWidth: '120px', fontWeight: 'bold' }}
                      value={publishCurrency}
                      onChange={(e) => setPublishCurrency(e.target.value)}
                    >
                      <option value="AED">AED</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="SAR">SAR</option>
                      <option value="QAR">QAR</option>
                      <option value="BHD">BHD</option>
                      <option value="OMR">OMR</option>
                      <option value="KWD">KWD</option>
                    </Form.Select>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={publishFeeAmount}
                      onChange={(e) => setPublishFeeAmount(e.target.value)}
                      placeholder="e.g. 100"
                      style={{ fontWeight: '600' }}
                    />
                  </InputGroup>
                  <Form.Text className="text-info d-block mt-2">
                    <i className="bi bi-info-circle me-1"></i>
                    New candidates will be "Unpublished" until they pay this fee.
                  </Form.Text>
                </Form.Group>
              </div>
            </div>

            <div className="settings-card p-4 rounded shadow-sm border mb-3 bg-white">
              <Form.Label className="fw-bold mb-3">Add-on Pricing (USD)</Form.Label>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Verified Tag</Form.Label>
                    <InputGroup size="sm">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control 
                        type="number" 
                        value={badgePrice} 
                        onChange={(e) => setBadgePrice(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Offer Badge</Form.Label>
                    <InputGroup size="sm">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control 
                        type="number" 
                        value={tagPrice} 
                        onChange={(e) => setTagPrice(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small">Tag + Badge</Form.Label>
                    <InputGroup size="sm">
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control 
                        type="number" 
                        value={bothPrice} 
                        onChange={(e) => setBothPrice(e.target.value)}
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', padding: '0 30px 30px', backgroundColor: '#fdfdfd' }}>
          <Button
            variant="light"
            onClick={() => setShowSettingsModal(false)}
            className="px-4 py-2 text-muted fw-bold rounded-pill"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveSettings}
            className="px-5 py-2 fw-bold shadow rounded-pill"
            style={{ backgroundColor: '#004a8c', border: 'none' }}
          >
            Update Settings
          </Button>
        </Modal.Footer>
      </Modal >

      {/* Assign Badge Modal */}
      <Modal show={showBadgeModal} onHide={() => setShowBadgeModal(false)} centered className="premium-modal">
        <Modal.Header closeButton style={{ border: 'none', padding: '25px 30px 10px' }}>
          <Modal.Title className="fw-bold" style={{ color: '#004a8c', fontSize: '1.25rem' }}>
            Assign Offer Badge
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '30px' }}>
          <Form.Group>
            <Form.Label className="fw-bold">Badge Text</Form.Label>
            <Form.Control 
              type="text" 
              value={badgeText} 
              onChange={(e) => setBadgeText(e.target.value)}
              placeholder="e.g. QCHP Licensed, Premium Candidate"
              className="py-2"
            />
            <Form.Text className="text-muted">
              This text will appear on the candidate's profile photo.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ border: 'none', padding: '0 30px 30px' }}>
          <Button variant="light" onClick={() => setShowBadgeModal(false)} className="rounded-pill px-4">
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveBadge} 
            className="rounded-pill px-4"
            style={{ backgroundColor: '#004a8c', border: 'none' }}
          >
            Save Badge
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ManageEmployee;
